import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation, useQuery } from "convex/react";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { useNavigate } from "react-router";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { authClient } from "../../lib/authClient";
import { Button } from "../ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";

/**
 * Sidebar header switcher. Staff: lists every firm; picking one calls switchToFirm
 * (ensures a Better Auth member row exists) then flips active org + reloads.
 * Non-staff: read-only label with the current firm name.
 */
export default function OrgSwitcher() {
	const navigate = useNavigate();
	const isStaff = useQuery(api.staff.isCurrentUserStaff);
	const viewer = useQuery(api.lib.viewer.getCurrentSession);
	const firms = useQuery(api.staff.listAllFirms, isStaff ? {} : "skip");
	const switchToFirm = useMutation(api.staff.switchToFirm);

	const {
		mutate,
		isPending,
		error: switchError,
	} = useReactQueryMutation({
		mutationFn: async (tenantId: Id<"tenants">) => {
			const { authOrgId } = await switchToFirm({ tenantId });
			await authClient.organization.setActive({ organizationId: authOrgId });
			// ConvexBetterAuthProvider memoises fetchAccessToken by sessionId; refresh needed for new org claim.
			navigate(0);
		},
	});

	const currentName = viewer?.tenantName ?? "Loading...";
	const switchErrorMsg =
		switchError instanceof Error ? switchError.message : null;

	if (isStaff === undefined) return null;

	if (!isStaff) {
		return (
			<div className="rounded-md border border-sidebar-border/60 bg-sidebar-accent/20 px-3 py-2 text-sm font-medium text-sidebar-foreground group-data-[collapsible=icon]:hidden">
				{currentName}
			</div>
		);
	}

	return (
		<div>
			{switchErrorMsg && (
				<p className="mb-1 truncate text-xs text-destructive">
					{switchErrorMsg}
				</p>
			)}
			<DropdownMenu>
				<DropdownMenuTrigger
					render={
						<Button
							variant="ghost"
							className="w-full justify-between px-3 text-left font-medium group-data-[collapsible=icon]:hidden"
							disabled={isPending}
						/>
					}
				>
					<span className="truncate">
						{isPending ? "Switching..." : currentName}
					</span>
					<ChevronsUpDownIcon className="size-4 shrink-0 text-muted-foreground" />
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start" className="w-64">
					{firms === undefined ? (
						<DropdownMenuItem disabled>Loading firms...</DropdownMenuItem>
					) : firms.length === 0 ? (
						<DropdownMenuItem disabled>No firms yet</DropdownMenuItem>
					) : (
						firms.map((f) => {
							const isActive = f.tenantId === viewer?.tenantId;
							return (
								<DropdownMenuItem
									key={f.tenantId}
									onClick={() => !isActive && mutate(f.tenantId)}
									className="flex items-center justify-between gap-2"
								>
									<span className="truncate">{f.name}</span>
									{isActive ? (
										<CheckIcon className="size-4 text-foreground" />
									) : null}
								</DropdownMenuItem>
							);
						})
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
